/**
 * Barked items on this device (docs/ux/project-tab-spec.md §1.2, §3.2): the device writes the
 * sealed `bark` entity (its verbatim text, attachments, later user messages and confirmations)
 * and signs `bark.submit` / `bark.control` to the project's triage machine. The hub sees only
 * ciphertext entity revisions and sealed commands addressed by machine id.
 *
 * Submit is optimistic (the row shows at once) and durable: an offline bark waits in the local
 * outbox and is sent exactly once on reconnect (the entity id is fixed at submit; the daemon's
 * queue is idempotent per bark id).
 */
import {
  type BarkAttachment,
  type BarkControlAction,
  type BarkMode,
  type BarkProposal,
  type BarkRecord,
  backoffDelay,
  barkBody,
  type CommandKind,
  type CommandPayload,
  hydrateBark,
  mergeBark,
  newBark,
  type PrivateProjectBinding,
  type ProposedOp,
  rateLimitRetryAfter,
} from '@viko/protocol'
import { proposalDigest } from './discuss-store.js'
import { idb } from './idb.js'
import type { Machine, Project } from './types.js'
import type { PendingFile } from './uploads.js'

export interface BarkEntities {
  list(kind: 'bark'): Promise<{ id: string; value: Record<string, unknown> }[]>
  read(kind: 'bark', id: string): Promise<{ id: string; value: Record<string, unknown> }>
  write(kind: 'bark', id: string, value: Record<string, unknown>): Promise<unknown>
}

export interface BarkBackend {
  /** Undefined while locked (no content key on this device). */
  entities(): BarkEntities | undefined
  dispatch<K extends Extract<CommandKind, 'bark.submit' | 'bark.control'>>(
    kind: K,
    machineId: string,
    payload: CommandPayload<K>,
  ): Promise<unknown>
  /** The signed project binding (same as `run.start`) so the machine finds its checkout. */
  binding?(machineId: string, projectId: string): Promise<PrivateProjectBinding | undefined>
  upload?(files: PendingFile[], scope: string, machineId: string): Promise<BarkAttachment[]>
}

export interface OutboxKv {
  get<T>(key: string): Promise<T | undefined>
  update<T>(key: string, change: (current: T | undefined) => T): Promise<T>
}

interface Queued {
  id: string
  projectId: string
  machineId: string
  value: Record<string, unknown>
}

/** The project's triage machine: first online brain machine, else any online project machine. */
export function pickTriageMachine(
  project: Pick<Project, 'machines' | 'settings'> | undefined,
  machines: Pick<Machine, 'id' | 'online'>[],
): string | undefined {
  const scoped = project ? machines.filter((m) => project.machines.includes(m.id)) : []
  const pool = scoped.length ? scoped : machines
  const brain = project?.settings?.brainMachines ?? []
  const ordered = [
    ...brain.flatMap((id) => pool.filter((m) => m.id === id)),
    ...pool.filter((m) => !brain.includes(m.id)),
  ]
  return (ordered.find((m) => m.online) ?? ordered[0])?.id
}

export class BarkStore {
  private readonly local = new Map<string, BarkRecord>()
  private readonly offline = new Set<string>()
  private readonly listeners = new Set<() => void>()
  private revision = 0
  private flushing: Promise<void> | undefined

  constructor(
    private readonly be: BarkBackend,
    private readonly kv: OutboxKv = idb,
    private readonly now: () => number = Date.now,
    private readonly outboxKey = 'bark-outbox/v1',
    /** Project id → the item's opaque project id (identity for encrypted accounts). */
    private readonly projectKey: (projectId: string) => string = (id) => id,
  ) {}

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }
  version = () => this.revision
  private emit() {
    this.revision++
    for (const l of this.listeners) l()
  }

  get available(): boolean {
    return !!this.be.entities()
  }

  /** Waiting in the offline outbox ("Queued offline — sends when you're back"). */
  isQueuedOffline(id: string): boolean {
    return this.offline.has(id)
  }

  /** This project's items, newest first; optimistic local writes win until the relay has them. */
  async list(projectId: string): Promise<BarkRecord[]> {
    const ents = this.be.entities()
    const rows = ents ? await ents.list('bark') : []
    const out = new Map<string, BarkRecord>()
    for (const r of rows) {
      try {
        const b = hydrateBark(r)
        if (b.projectId === this.projectKey(projectId)) out.set(b.id, b)
      } catch {
        /* a malformed or foreign record is skipped, never shown */
      }
    }
    for (const b of this.local.values()) {
      if (b.projectId !== this.projectKey(projectId)) continue
      const remote = out.get(b.id)
      if (!remote || remote.updatedAt < b.updatedAt)
        out.set(b.id, remote ? mergeBark(remote, b) : b)
      else this.local.delete(b.id)
    }
    return [...out.values()].sort((a, b) => b.createdAt - a.createdAt)
  }

  async get(id: string): Promise<BarkRecord | undefined> {
    const ents = this.be.entities()
    try {
      if (ents) return hydrateBark(await ents.read('bark', id))
    } catch {
      /* fall through to the optimistic copy */
    }
    return this.local.get(id)
  }

  /** Write the item and send it; resolves as soon as the optimistic row exists. */
  async submit(i: {
    projectId: string
    machineId: string
    text: string
    mode: BarkMode
    dictated?: boolean
    files?: PendingFile[]
    hints?: BarkRecord['hints']
    /** bark-route: binding routing constraints (absent/empty = Auto). */
    routing?: BarkRecord['routing']
    source?: 'tab' | 'capture' | 'notification' | 'direct'
  }): Promise<BarkRecord> {
    const id = crypto.randomUUID()
    const attachments =
      i.files?.length && this.be.upload
        ? await this.be.upload(i.files, `bark-${id}`, i.machineId)
        : []
    const record = newBark({
      id,
      projectId: this.projectKey(i.projectId),
      mode: i.mode,
      text: i.text,
      dictated: i.dictated ?? false,
      attachments,
      ...(i.hints ? { hints: i.hints } : {}),
      ...(i.routing ? { routing: i.routing } : {}),
      source: i.source ?? 'tab',
      clientId: crypto.randomUUID(),
      messageId: `u-${crypto.randomUUID().slice(0, 12)}`,
      now: this.now(),
    })
    this.local.set(id, record)
    this.offline.add(id)
    this.emit()
    const entry: Queued = {
      id,
      projectId: record.projectId,
      machineId: i.machineId,
      value: barkBody(record),
    }
    await this.kv.update<Queued[]>(this.outboxKey, (cur) => [
      ...(cur ?? []).filter((q) => q.id !== id),
      entry,
    ])
    void this.flush()
    return record
  }

  /** Send everything in the outbox (on submit, on reconnect, on unlock). Exactly once per bark. */
  flush(): Promise<void> {
    this.flushing ??= this.flushAll().finally(() => {
      this.flushing = undefined
    })
    return this.flushing
  }

  private async flushAll() {
    const queued = (await this.kv.get<Queued[]>(this.outboxKey)) ?? []
    for (const q of queued) {
      const ents = this.be.entities()
      if (!ents) return
      try {
        let written = false
        try {
          await ents.read('bark', q.id)
          written = true
        } catch {
          /* not on the relay yet */
        }
        if (!written) await ents.write('bark', q.id, q.value)
        const binding = await this.be.binding?.(q.machineId, q.projectId).catch(() => undefined)
        await this.be.dispatch('bark.submit', q.machineId, {
          barkId: q.id,
          projectId: q.projectId,
          ...(binding ? { privateProject: binding } : {}),
        })
        await this.kv.update<Queued[]>(this.outboxKey, (cur) =>
          (cur ?? []).filter((x) => x.id !== q.id),
        )
        this.offline.delete(q.id)
        this.emit()
      } catch {
        this.offline.add(q.id)
        this.emit()
        return // offline or relay down: keep order, retry later
      }
    }
  }

  /** Read-merge-write (the daemon writes too; arrays merge by id). */
  async update(b: BarkRecord, mutate: (cur: BarkRecord) => BarkRecord): Promise<BarkRecord> {
    const ents = this.be.entities()
    if (!ents) throw new Error('Unlock to send')
    for (let n = 0; n < 4; n++) {
      const latest = hydrateBark(await ents.read('bark', b.id))
      const next = mergeBark(latest, { ...mutate(latest), updatedAt: this.now() })
      try {
        await ents.write('bark', b.id, barkBody(next))
        this.local.set(b.id, next)
        this.emit()
        return next
      } catch (e) {
        // burst-guard: a rate-limited relay is not a write conflict; never spin on it.
        if (n === 3 || rateLimitRetryAfter(e) !== undefined) throw e
        await new Promise((r) => setTimeout(r, backoffDelay(n, { baseMs: 200, maxMs: 2_000 })))
      }
    }
    throw new Error('Could not save')
  }

  private machine(b: BarkRecord, fallback?: string): string {
    const m = b.triage.machineId ?? fallback
    if (!m) throw new Error('No machine can sort this yet.')
    return m
  }

  async control(
    b: BarkRecord,
    action: BarkControlAction,
    extra: Partial<Omit<CommandPayload<'bark.control'>, 'barkId' | 'projectId' | 'action'>> = {},
    machineId?: string,
  ) {
    const out = await this.be.dispatch('bark.control', this.machine(b, machineId), {
      barkId: b.id,
      projectId: b.projectId,
      action,
      ...extra,
    })
    // The machine rewrote the item (redirect, undo, ...): refetch it now instead of waiting for
    // the idle poll, which is quiet (60 s) once everything shown is settled.
    this.emit()
    return out
  }

  /** A user message in the item's conversation (also "Add context" on a quick bark). */
  async message(
    b: BarkRecord,
    text: string,
    o: { files?: PendingFile[]; machineId?: string } = {},
  ): Promise<BarkRecord> {
    const machineId = this.machine(b, o.machineId)
    const attachments =
      o.files?.length && this.be.upload
        ? await this.be.upload(o.files, `bark-${b.id}`, machineId)
        : []
    const id = `u-${crypto.randomUUID().slice(0, 12)}`
    const at = this.now()
    const saved = await this.update(b, (cur) => ({
      ...cur,
      thread: [
        ...cur.thread,
        { id, at, author: 'user' as const, text, ...(attachments.length ? { attachments } : {}) },
      ],
    }))
    await this.control(saved, 'message', { messageId: id }, machineId)
    return saved
  }

  /**
   * "Send straight to this run instead" (§1.2): skips triage, recorded as an item with outcome
   * `direct` so it still shows what happened (the zero-overhead benchmark path).
   */
  async recordDirect(i: {
    projectId: string
    machineId: string
    runId: string
    text: string
    send: (clientMessageId: string) => Promise<unknown>
  }): Promise<BarkRecord> {
    const id = crypto.randomUUID()
    const now = this.now()
    const messageId = `u-${crypto.randomUUID().slice(0, 12)}`
    const base = newBark({
      id,
      projectId: this.projectKey(i.projectId),
      mode: 'quick',
      text: i.text,
      hints: { runId: i.runId },
      source: 'direct',
      clientId: crypto.randomUUID(),
      messageId,
      now,
    })
    const record: BarkRecord = {
      ...base,
      status: 'handled',
      triage: { ...base.triage, machineId: i.machineId },
      interpretation: 'Sent straight to the run you were looking at.',
      actions: [
        {
          id: 'act-direct',
          decision: 'direct',
          targets: [{ type: 'run', id: i.runId, role: 'steered' }],
          assumptions: [],
          ops: [{ op: 'run.steered', runId: i.runId, messageId }],
          status: 'active',
          at: now,
        },
      ],
    }
    await i.send(messageId)
    this.local.set(id, record)
    this.emit()
    const ents = this.be.entities()
    if (ents) await ents.write('bark', id, barkBody(record))
    // segmentation: a background scope check (daemon-side, no model call) may offer to move an
    // unrelated message to a new item; the words are already delivered either way.
    if (ents) void this.control(record, 'scope-check', {}, i.machineId).catch(() => {})
    return record
  }

  /** "Do it" / "Adjust…": the digest binds the daemon to exactly what this card shows. */
  async confirm(b: BarkRecord, p: BarkProposal, adjusted?: ProposedOp[]) {
    const proposalDigest_ = await proposalDigest(p, adjusted ?? p.actions)
    return this.control(b, adjusted ? 'adjust' : 'confirm', {
      proposalId: p.id,
      proposalDigest: proposalDigest_,
      ...(adjusted ? { adjusted } : {}),
    })
  }
}

// --- one store per backend -----------------------------------------------------------------------

const stores = new WeakMap<object, BarkStore>()
export function barkStoreFor(owner: object, make: () => BarkStore): BarkStore {
  let s = stores.get(owner)
  if (!s) {
    s = make()
    stores.set(owner, s)
  }
  return s
}
